import { forwardRef } from 'react';
import { Alert } from '../atoms/Alert/Alert';
import { Text } from '../atoms/Typography/Text';
import { MarkdownContent } from '../markdown/MarkdownContent';
import { useTranslation } from '../../i18n/useTranslation';
import type { MentorMessage } from '../../services/mentorService';
import './Mentor.css';

export interface MentorMessageListProps {
  messages: readonly MentorMessage[];
  /** The question currently being answered (shown until the answer arrives). */
  pendingQuestion?: string | null;
  /** The answer to pendingQuestion received so far (streamed). */
  streamText?: string;
}

function formatCredits(value: number, language: string): string {
  return new Intl.NumberFormat(language, { maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(value);
}

/**
 * The messages of one conversation as an ordered list. Every message is
 * labelled with its author in text ("You" / "Mentor"), not by colour or side
 * only. Assistant answers are rendered with the shared safe Markdown renderer
 * (React elements only, no HTML injection, safe links only); an answer cut off
 * at the output limit carries a text note. While an answer streams in, it
 * grows in place (aria-busy, no live announcement per delta — the page
 * announces once when it is complete).
 */
export const MentorMessageList = forwardRef<HTMLOListElement, MentorMessageListProps>(function MentorMessageList(
  { messages, pendingQuestion = null, streamText = '' },
  ref,
) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'en';
  const time = new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <ol ref={ref} className="mentor-messages" aria-label={t('mentor.chat.messagesLabel')} data-testid="mentor-messages">
      {messages.map((message) => (
        <li key={message.id} className={`mentor-message mentor-message--${message.role}`} data-testid={`mentor-message-${message.role}`}>
          <Text as="span" size="sm" weight="semibold" className="mentor-message__author">
            {message.role === 'user' ? t('mentor.chat.you') : t('mentor.chat.mentor')}
          </Text>
          {message.role === 'assistant' ? (
            <MarkdownContent source={message.content} baseLevel={3} className="mentor-message__markdown" newTabLabel={t('legal.opensInNewTab')} />
          ) : (
            <Text className="mentor-message__text">{message.content}</Text>
          )}
          {message.role === 'assistant' && message.truncated === true && (
            <Alert tone="info" compact live="off" className="mentor-message__note">
              {t('mentor.chat.truncated')}
            </Alert>
          )}
          <Text variant="caption" className="mentor-message__meta">
            <time dateTime={message.createdAt}>{time.format(new Date(message.createdAt))}</time>
            {message.role === 'assistant' && message.credits !== null
              ? ` · ${t('mentor.chat.credits', { credits: formatCredits(message.credits, language) })}`
              : ''}
            {message.role === 'assistant' && message.photoCount > 0 ? ` · ${t('mentor.chat.photosUsed', { count: message.photoCount })}` : ''}
          </Text>
        </li>
      ))}
      {pendingQuestion !== null && (
        <>
          <li className="mentor-message mentor-message--user mentor-message--pending">
            <Text as="span" size="sm" weight="semibold" className="mentor-message__author">
              {t('mentor.chat.you')}
            </Text>
            <Text className="mentor-message__text">{pendingQuestion}</Text>
          </li>
          <li
            className="mentor-message mentor-message--assistant mentor-message--pending"
            aria-busy="true"
            data-testid="mentor-message-streaming"
          >
            <Text as="span" size="sm" weight="semibold" className="mentor-message__author">
              {t('mentor.chat.mentor')}
            </Text>
            {streamText !== '' && (
              <MarkdownContent source={streamText} baseLevel={3} className="mentor-message__markdown" newTabLabel={t('legal.opensInNewTab')} />
            )}
            <Text variant="caption" className="mentor-message__status">
              {streamText === '' ? t('mentor.chat.thinking') : t('mentor.chat.writing')}
            </Text>
          </li>
        </>
      )}
    </ol>
  );
});
